<template>
  <div
    ref="rootRef"
    class="conversation-search-bar"
    role="search"
    :aria-label="t('chat.messageSearch.open')"
    data-test-id="conversation-search-bar"
  >
    <InputGroup class="conversation-search-bar__field">
      <InputGroupAddon>
        <Search aria-hidden="true" />
      </InputGroupAddon>
      <InputGroupInput
        v-model="queryRef"
        type="search"
        :placeholder="t('chat.messageSearch.placeholder')"
        :aria-label="t('chat.messageSearch.placeholder')"
        data-test-id="conversation-search-input"
        @keydown="onKeydown"
      />
    </InputGroup>
    <span class="conversation-search-bar__count" aria-live="polite" data-test-id="conversation-search-count">
      {{ countLabelRef }}
    </span>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      :aria-label="t('chat.messageSearch.older')"
      :disabled="!canGoOlderRef"
      data-test-id="conversation-search-older"
      @click="goToOlder"
    >
      <ChevronUp class="size-5" aria-hidden="true" />
    </Button>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      :aria-label="t('chat.messageSearch.newer')"
      :disabled="!canGoNewerRef"
      data-test-id="conversation-search-newer"
      @click="goToNewer"
    >
      <ChevronDown class="size-5" aria-hidden="true" />
    </Button>
    <Button
      type="button"
      variant="ghost"
      size="icon"
      :aria-label="t('chat.messageSearch.close')"
      data-test-id="conversation-search-close"
      @click="emit('close')"
    >
      <X class="size-5" aria-hidden="true" />
    </Button>
  </div>
</template>

<script setup lang="ts">
import { refDebounced } from '@vueuse/core'
import { ChevronDown, ChevronUp, Search, X } from 'lucide-vue-next'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group'
import { findSearchRanges, normalizeSearchText } from '@/features/chat/utils/chat-search'
import { useI18n } from '@/lib/i18n'

// Recherche dans la conversation ouverte. Elle lit le texte affiche des bulles
// (mentions comprises, telles qu'on les voit) plutot que le contenu brut, et
// surligne les occurrences avec la CSS Custom Highlight API : aucun noeud du
// fil n'est modifie, donc rien a defaire quand Vue re-rend un message.
const props = defineProps<{
  feed: HTMLElement | null
  // nombre de messages du fil : relance la recherche quand un message arrive
  messagesCount: number
}>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()

const CURRENT_CLASS = 'is-search-current'
const HIGHLIGHT_ALL = 'chat-search'
const HIGHLIGHT_CURRENT = 'chat-search-current'

const rootRef = ref<HTMLElement | null>(null)
const queryRef = ref('')
const debouncedQueryRef = refDebounced(queryRef, 150)
// ids des messages trouves, du plus ancien au plus recent
const matchesRef = ref<string[]>([])
const currentIndexRef = ref(-1)

const supportsHighlights = typeof CSS !== 'undefined' && 'highlights' in CSS && 'Highlight' in window

const hasQueryRef = computed(() => debouncedQueryRef.value.trim().length > 0)
const canGoOlderRef = computed(() => currentIndexRef.value > 0)
const canGoNewerRef = computed(
  () => currentIndexRef.value !== -1 && currentIndexRef.value < matchesRef.value.length - 1,
)
const countLabelRef = computed(() => {
  if (!hasQueryRef.value) return ''
  if (matchesRef.value.length === 0) return t('chat.messageSearch.noResult')
  return t('chat.messageSearch.count', { current: currentIndexRef.value + 1, total: matchesRef.value.length })
})

function getBubbles() {
  if (!props.feed) return []
  return [...props.feed.querySelectorAll<HTMLElement>('[data-message-id]')].flatMap((wrapper) => {
    const bubble = wrapper.querySelector<HTMLElement>('[data-test-id="message-bubble"]')
    return bubble && wrapper.dataset.messageId ? [{ id: wrapper.dataset.messageId, wrapper, bubble }] : []
  })
}

function clearPaint() {
  props.feed?.querySelectorAll(`.${CURRENT_CLASS}`).forEach((element) => element.classList.remove(CURRENT_CLASS))
  if (supportsHighlights) {
    CSS.highlights.delete(HIGHLIGHT_ALL)
    CSS.highlights.delete(HIGHLIGHT_CURRENT)
  }
}

function paint() {
  clearPaint()
  const currentId = matchesRef.value[currentIndexRef.value]
  const query = debouncedQueryRef.value.trim()
  const all: Range[] = []
  const current: Range[] = []
  for (const { id, wrapper, bubble } of getBubbles()) {
    if (!matchesRef.value.includes(id)) continue
    if (id === currentId) wrapper.classList.add(CURRENT_CLASS)
    if (!supportsHighlights) continue
    const walker = document.createTreeWalker(bubble, NodeFilter.SHOW_TEXT)
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      for (const [start, end] of findSearchRanges(node.textContent ?? '', query)) {
        const range = document.createRange()
        range.setStart(node, start)
        range.setEnd(node, end)
        ;(id === currentId ? current : all).push(range)
      }
    }
  }
  if (supportsHighlights) {
    CSS.highlights.set(HIGHLIGHT_ALL, new window.Highlight(...all))
    CSS.highlights.set(HIGHLIGHT_CURRENT, new window.Highlight(...current))
  }
}

function scrollToCurrent() {
  const currentId = matchesRef.value[currentIndexRef.value]
  if (!currentId || !props.feed) return
  props.feed
    .querySelector(`[data-message-id="${CSS.escape(currentId)}"]`)
    ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

// scroll : vrai quand la recherche change (on va au resultat), faux quand un
// message arrive (on garde le resultat en cours et la position de lecture)
function computeMatches(scroll: boolean) {
  const query = normalizeSearchText(debouncedQueryRef.value.trim())
  const previousId = matchesRef.value[currentIndexRef.value]
  if (!query) {
    matchesRef.value = []
    currentIndexRef.value = -1
    clearPaint()
    return
  }
  const ids = getBubbles()
    .filter(({ bubble }) => normalizeSearchText(bubble.textContent ?? '').includes(query))
    .map(({ id }) => id)
  matchesRef.value = ids
  const kept = !scroll && previousId ? ids.indexOf(previousId) : -1
  // on part du resultat le plus recent, comme dans les messageries courantes
  currentIndexRef.value = kept !== -1 ? kept : ids.length - 1
  paint()
  if (scroll) scrollToCurrent()
}

function goTo(index: number) {
  if (index < 0 || index >= matchesRef.value.length) return
  currentIndexRef.value = index
  paint()
  scrollToCurrent()
}

const goToOlder = () => goTo(currentIndexRef.value - 1)
const goToNewer = () => goTo(currentIndexRef.value + 1)

// Entree : resultat plus ancien ; Maj+Entree : plus recent ; Echap : fermer
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault()
    if (event.shiftKey) goToNewer()
    else goToOlder()
  } else if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
  }
}

watch(debouncedQueryRef, () => nextTick(() => computeMatches(true)))
watch(
  () => props.messagesCount,
  () => nextTick(() => computeMatches(false)),
)

onMounted(() => rootRef.value?.querySelector('input')?.focus())
onBeforeUnmount(clearPaint)
</script>

<style lang="scss">
@import '@/styles/utils';
@import '@/features/chat/styles/chat-layout';

.conversation-search-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: calc($s / 2);
  padding: $s * 1.25 $s * 3;
  border-bottom: 1px solid var(--border);
  background-color: var(--background);

  @include conversation-compact {
    padding: $s $s * 2;
  }

  &__field {
    flex: 1;
    min-width: 0;
    max-width: 26rem;
  }

  &__count {
    padding: 0 $s;
    font-size: 0.8125rem;
    font-variant-numeric: tabular-nums;
    color: var(--muted-foreground);
    white-space: nowrap;
  }
}

// occurrences surlignees dans les bulles (CSS Custom Highlight API)
::highlight(chat-search) {
  background-color: #fef3c7;
  color: inherit;
}

::highlight(chat-search-current) {
  background-color: #fcd34d;
  color: #1b1b1e;
}

// resultat en cours : la bulle est cernee, utile aussi sans la Highlight API
.messages-block-feed .is-search-current [data-test-id='message-bubble'] {
  box-shadow: 0 0 0 2px var(--ring);
}
</style>
